<script lang="ts">
  import {
    Activity,
    BarChart3,
    Briefcase,
    LineChart,
    LogOut,
    Menu,
    Monitor,
    Palette,
    User,
  } from 'lucide-svelte';
  import { location, push } from 'svelte-spa-router';
  import { auth, logout } from '$lib/stores/auth.svelte';
  import { clock, fmtTimeOfDay } from '$lib/stores/clock.svelte';
  import { livePriceStore } from '$lib/stores/livePrice.svelte';
  import { setTheme, themeStore } from '$lib/stores/theme.svelte';
  import {
    isLiveLookForced,
    liveLookStore,
    setLiveLooksLikeDev,
  } from '$lib/stores/liveLook.svelte';

  import { Avatar, AvatarFallback, AvatarImage } from '$lib/components/ui/avatar';
  import { Button } from '$lib/components/ui/button';
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
  } from '$lib/components/ui/dropdown-menu';
  import { Separator } from '$lib/components/ui/separator';
  import { Sheet, SheetContent, SheetTrigger } from '$lib/components/ui/sheet';
  import { TooltipProvider } from '$lib/components/ui/tooltip';
  import LivePriceTicker from './LivePriceTicker.svelte';
  import { onDestroy, onMount } from 'svelte';
  import { fetchLiveMonitorSummary } from '$lib/api/liveMonitor';
  import { type ScheduledTask, scheduleAlignedRefresh } from '$lib/utils/schedule';

  let { children } = $props();

  // ----- LivePriceTicker: subscribe global a TODOS los simbolos live -----
  // El ticker del topbar debe mostrar cotizacion siempre, en cualquier ruta.
  // AppShell es el shell que envuelve todas las paginas, asi que aca corremos
  // el fetch periodico de la lista de sessions live (todas, no solo las del
  // mode actual) y seteamos sus simbolos al store.
  //
  // No esperamos al primer minute close: las sessions live pueden empezar
  // en cualquier momento y queremos que el ticker se actualice rapido.
  //
  // refresh aligned :05 cada minuto (mismo patron que el resto del dashboard).
  let liveTickerTask: ScheduledTask | null = null;

  async function refreshTickerSymbols(): Promise<void> {
    try {
      // mode=live ya filtra developerMonitor !== true. Para que el ticker
      // tambien refleje sessions developer, hacemos los dos modes en paralelo.
      const [liveRes, devRes] = await Promise.allSettled([
        fetchLiveMonitorSummary('live'),
        fetchLiveMonitorSummary('developer'),
      ]);
      const symbols = new Set<string>();
      if (liveRes.status === 'fulfilled') {
        for (const s of liveRes.value.sessions) if (s.symbol) symbols.add(s.symbol);
      }
      if (devRes.status === 'fulfilled') {
        for (const s of devRes.value.sessions) if (s.symbol) symbols.add(s.symbol);
      }
      livePriceStore.setSymbols(symbols);
    } catch (err) {
      console.warn('[app-shell] ticker refresh fallo', err);
    }
  }

  onMount(() => {
    // Arrancamos el WS proxy (singleton; idempotente).
    livePriceStore.start();
    // Primer fetch inmediato + schedule al :05 de cada minuto.
    void refreshTickerSymbols();
    liveTickerTask = scheduleAlignedRefresh(() => refreshTickerSymbols(), 5);
  });

  onDestroy(() => {
    liveTickerTask?.cancel();
    // No paramos el store: el shell es singleton; si se desmonta es porque
    // la app entera se va (e.g. logout). El WS se cierra solo con el browser.
  });

  // Main tabs heredadas de v1 (CLAUDE.md + 00-diagnostico.md).
  // /sessions y /live aterrizan en Fase 3-4; el resto stub hasta su fase.
  const navItems = [
    { href: '/sessions', label: 'Sessions', icon: Briefcase },
    { href: '/live', label: 'Live Monitor', icon: Monitor },
    { href: '/developer', label: 'Developer', icon: BarChart3 },
    { href: '/charts', label: 'Charts', icon: LineChart },
    { href: '/vix', label: 'VIX', icon: Activity },
  ];

  let mobileNavOpen = $state(false);

  function navigate(href: string): void {
    push(href);
    mobileNavOpen = false;
  }

  function userInitials(name: string | undefined): string {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0]!.charAt(0).toUpperCase();
    return (parts[0]!.charAt(0) + parts[parts.length - 1]!.charAt(0)).toUpperCase();
  }

  async function handleLogout(): Promise<void> {
    await logout();
    push('/login');
  }
</script>

<TooltipProvider>
  <div class="bg-background text-foreground flex min-h-screen flex-col">
    <!-- Topbar -->
    <header
      class="bg-card-luxury border-gold/15 sticky top-0 z-40 flex h-14 items-center gap-3 border-b px-4 shadow-[0_1px_0_0_hsl(var(--gold)/0.06)] md:px-6"
    >
      <!-- Mobile menu trigger -->
      <Sheet bind:open={mobileNavOpen}>
        <SheetTrigger>
          {#snippet child({ props })}
            <Button {...props} variant="ghost" size="icon" class="md:hidden">
              <Menu class="size-5" />
              <span class="sr-only">Open menu</span>
            </Button>
          {/snippet}
        </SheetTrigger>
        <SheetContent side="left" class="w-72 p-0">
          <nav class="flex h-full flex-col gap-1 p-4">
            <div class="mb-4 flex items-center gap-2 px-2">
              <img src="/rabbyt-mascot.png" alt="RabbytHole" class="size-7" />
              <span class="text-display text-base">RabbytHole</span>
            </div>
            {#each navItems as item (item.href)}
              {@const active = $location === item.href || $location.startsWith(`${item.href}/`)}
              <button
                type="button"
                class={[
                  'hover:bg-accent hover:text-accent-foreground flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                  active && 'bg-accent text-accent-foreground',
                ].filter(Boolean).join(' ')}
                onclick={() => navigate(item.href)}
              >
                <item.icon class="size-4" />
                {item.label}
              </button>
            {/each}
          </nav>
        </SheetContent>
      </Sheet>

      <!-- Logo + brand -->
      <button
        type="button"
        class="flex items-center gap-2 transition-opacity hover:opacity-80"
        onclick={() => push('/dashboard')}
      >
        <img src="/rabbyt-mascot.png" alt="RabbytHole" class="size-9" />
        <span class="text-display hidden text-lg sm:inline">RabbytHole Dashboard</span>
      </button>

      <!-- Desktop nav (inline en topbar) -->
      <nav class="ml-4 hidden items-center gap-1 md:flex">
        {#each navItems as item (item.href)}
          {@const active = $location === item.href || $location.startsWith(`${item.href}/`)}
          <Button
            variant={active ? 'secondary' : 'ghost'}
            size="sm"
            onclick={() => push(item.href)}
          >
            <item.icon class="size-4" />
            {item.label}
          </Button>
        {/each}
      </nav>

      <!-- Live price ticker + clock (entre nav y user controls) -->
      <div class="ml-auto flex items-center gap-3">
        <LivePriceTicker />

        <div
          class="hidden items-center gap-1.5 font-mono text-base font-medium tabular-nums sm:flex"
          title="Local time"
        >
          <span class="text-primary text-lg">{fmtTimeOfDay(clock.now)}</span>
        </div>
      </div>

      <div class="flex items-center gap-2">
        <!-- Palette switcher (Rabbit naranja / Classic verde) -->
        <DropdownMenu>
          <DropdownMenuTrigger>
            {#snippet child({ props })}
              <Button {...props} variant="ghost" size="icon" aria-label="Switch palette">
                <Palette class="size-4" />
              </Button>
            {/snippet}
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Palette</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onclick={() => setTheme('rabbit')}>
              <span
                class="mr-1 inline-block size-3 rounded-full"
                style="background:#D87807"
              ></span>
              Rabbit
              {#if themeStore.theme === 'rabbit'}
                <span class="ml-auto text-xs">●</span>
              {/if}
            </DropdownMenuItem>
            <DropdownMenuItem onclick={() => setTheme('classic')}>
              <span
                class="mr-1 inline-block size-3 rounded-full"
                style="background:#87a96b"
              ></span>
              Classic
              {#if themeStore.theme === 'classic'}
                <span class="ml-auto text-xs">●</span>
              {/if}
            </DropdownMenuItem>

            <DropdownMenuSeparator />
            <DropdownMenuLabel>Live Monitor</DropdownMenuLabel>
            <DropdownMenuItem
              onclick={() =>
                setLiveLooksLikeDev(!liveLookStore.liveLooksLikeDev, auth.user?.email ?? null)}
              disabled={isLiveLookForced(auth.user?.email ?? null)}
            >
              <span
                class="mr-1 inline-block size-3 rounded-full"
                style="background:#22d3ee"
              ></span>
              Live looks like Dev
              {#if liveLookStore.liveLooksLikeDev}
                <span class="ml-auto text-xs">●</span>
              {/if}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <Separator orientation="vertical" class="mx-1 h-6" />

        <!-- User menu -->
        <DropdownMenu>
          <DropdownMenuTrigger>
            {#snippet child({ props })}
              <button
                {...props}
                type="button"
                aria-label="Account menu"
                class="focus-visible:ring-ring rounded-full focus-visible:ring-2 focus-visible:outline-none"
              >
                <Avatar>
                  {#if auth.user?.picture}
                    <AvatarImage
                      src={auth.user.picture}
                      alt={auth.user?.name ?? ''}
                      referrerpolicy="no-referrer"
                    />
                  {/if}
                  <AvatarFallback>{userInitials(auth.user?.name)}</AvatarFallback>
                </Avatar>
              </button>
            {/snippet}
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" class="w-56">
            <DropdownMenuLabel class="font-normal">
              <div class="flex flex-col gap-0.5">
                <span class="text-sm font-medium leading-none">
                  {auth.user?.name ?? 'Not signed in'}
                </span>
                {#if auth.user?.email}
                  <span class="text-muted-foreground text-xs leading-none">
                    {auth.user.email}
                  </span>
                {/if}
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem disabled>
              <User class="size-4" />
              Profile
              <span class="text-muted-foreground ml-auto text-xs">soon</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onclick={handleLogout}>
              <LogOut class="size-4" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>

    <!-- Body: content -->
    <main class="flex-1">
      {@render children?.()}
    </main>
  </div>
</TooltipProvider>
